import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Tab } from "@headlessui/react";
import {
  ComputerDesktopIcon,
  DocumentCheckIcon,
  InboxIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { richContentOptions } from "@/components/content-options";
import Image from "next/image";
import { useEffect, useState } from "react";
import Modal from "@/components/modal";

import ProductSchema from "@/utils/product";
import PartnerModal, { PartnerData } from "@/components/elearning/partner-modal";
import { useSession } from "@/components/providers/session";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const offers = (vtct: boolean) => [
  {
    name: "Complete Training",
    description: vtct ? "Online Exam" : "For Immediate Certification",
    href: "#",
  },
  {
    name: "Original Content",
    description: "From Our Expert Tutors",
    href: "#",
  },
  {
    name: "Complete End To End",
    description: "Video Demonstrations",
    href: "#",
  },
];

const policies = (vtct: boolean) => [
  {
    name: vtct ? "VTCT Qualification" : "CPD Accredited Training Provider",
    icon: DocumentCheckIcon,
    description: "Vetted accredited training",
  },
  {
    name: "Fully Online Training",
    icon: ComputerDesktopIcon,
    description: "Train your way on any device",
  },
  {
    name: "Full Demonstration",
    icon: VideoCameraIcon,
    description: "Complete end to end treatment demonstration",
  },
  {
    name: vtct ? "Qualification" : "Immediate Certification",
    icon: InboxIcon,
    description: vtct
      ? "Schedule online exam after completion"
      : "Delivered immediately after completion",
  },
];

export default function OnlineBooking(props) {
  console.log("online", props);
  const [interested, setInterested] = useState<any>(false);
  const [query, setQuery] = useState<any>("");
  const { addOnlineCourses, setContext } = useSession();
  const [partner, setPartner] = useState<PartnerData | null>(null);

  useEffect(() => {
    try {
      const d = localStorage.getItem("cc.p");
      if (d) {
        const e = JSON.parse(d);
        console.log("e", e);
        setQuery(
          Object.keys(e)
            .map((k) => `${k}=${e[k]}`)
            .join("&")
        );
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  const { data } = props.primary.fields;

  const amount = props.primary.fields?.link
    ? props.primary.fields.link?.line_items.data.reduce(
        (a, b) => a + b.amount_total,
        0
      )
    : data.price
    ? Number(data.price) * 100
    : 0;

  const handleClick = (href: string) => {
    gtag("event", "begin_checkout", {
      event_callback: function () {
        window.location.href = href;
      },
    });
  };

  const priceBlock = data.was ? (
    <>
      <div className="flex items-start">
        <p className="tracking-tight text-base text-gray-900 mr-2">
          Was:{" "}
          {new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
          }).format(data.was / 100)}
        </p>
        <p className="tracking-tight text-3xl text-gray-900 flex items-start">
          <span className="tracking-tight text-base text-gray-900 mr-2">
            Now:
          </span>{" "}
          {new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
          }).format(amount / 100)}
        </p>
      </div>
    </>
  ) : (
    <p className="tracking-tight text-3xl text-gray-900">
      {new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(amount / 100)}
    </p>
  );

  const vtct = props.fields.title.toLowerCase().includes("vtct");

  return (
    <>
      <ProductSchema {...props} amount={amount} schemaType="online" />
      <div className="bg-white">
        <div className="max-w-2xl mx-auto pt-12 pb-16 px-4 sm:pb-18 sm:px-6 lg:max-w-7xl lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start">
            {/* Image gallery */}
            <Tab.Group as="div" className="flex flex-col-reverse">
              {/* Image selector */}
              <div className="hidden mt-6 w-full max-w-2xl mx-auto sm:block lg:max-w-none">
                <Tab.List className="grid grid-cols-4 gap-6">
                  {props?.fields?.images?.map((image) => (
                    <Tab
                      key={image.id}
                      className="relative h-24 bg-white rounded-md flex items-center justify-center text-sm font-medium uppercase text-gray-900 cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring focus:ring-offset-4 focus:ring-opacity-50"
                    >
                      {({ selected }) => (
                        <>
                          <span className="sr-only">{image.fields.title}</span>
                          <span className="absolute inset-0 rounded-md overflow-hidden">
                            <img
                              src={image.fields.file.url}
                              alt=""
                              className="w-full h-full object-center object-cover"
                            />
                          </span>
                          <span
                            className={classNames(
                              selected ? "ring-brand-500" : "ring-transparent",
                              "absolute inset-0 rounded-md ring-2 ring-offset-2 pointer-events-none"
                            )}
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </Tab>
                  ))}
                </Tab.List>
              </div>

              <Tab.Panels className="w-full aspect-w-16 aspect-h-9">
                {props?.fields?.images?.map((image) => (
                  <Tab.Panel key={image.sys.id}>
                    <img
                      src={image.fields.file.url}
                      alt={image.fields.title}
                      className="w-full h-full object-center object-cover rounded-lg"
                    />
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
            {/* Product info */}
            <div className="mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0">
              {!props.primary.fields.data.active &&
                props.primary.fields.data.release && (
                  <span className="mb-2 inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-brand-100 text-brand-800">
                    Release Date: {props.primary.fields.data.release}
                  </span>
                )}

              {vtct && (
                <div className="w-32 h-8 relative mb-2">
                  <Image
                    alt={props.fields.title}
                    src="https://images.ctfassets.net/eo68gvto3q0j/1M0VdXobETp4giEjEXVxON/97c2099676dc3066ddfdc057577c3883/image"
                    fill
                  />
                </div>
              )}
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                {props.fields.title}
              </h1>

              <h2 className="text-base text-brand-600 font-semibold tracking-wide uppercase max-w-lg">
                {props.fields.subtitle}
              </h2>

              <div className="mt-3">
                <h2 className="sr-only">Course information</h2>

                {priceBlock}
                {!props.primary.fields.data.active &&
                  props.primary.fields.data.full && (
                    <>
                      <p>Pre release special offer</p>
                      <p className="tracking-tight text-base text-gray-900">
                        Normal Price: £{props.primary.fields.data.full}
                      </p>
                    </>
                  )}
              </div>

              <div className="mt-6">
                <h3 className="sr-only">Description</h3>

                <div className="text-base text-gray-700 space-y-6">
                  {documentToReactComponents(
                    props.fields.body,
                    richContentOptions
                  )}
                </div>
              </div>

              <div className="mt-6">
                <div className="mt-10 flex sm:flex-col1">
                  {props.primary.fields.data.active &&
                  props.primary.fields?.link ? (
                    <a
                      href={`${props.primary.fields.link.url}?${query}`}
                      onClick={(e) => {
                        e.preventDefault();
                        addOnlineCourses(props.primary, {
                          online: true,
                        });
                        setContext((s) => ({
                          ...s,
                          onlineBasket: !s.onlineBasket,
                        }));
                      }}
                      className="max-w-xs flex-1 bg-brand-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-brand-500 sm:w-full"
                    >
                      Buy &amp; Train
                    </a>
                  ) : data.partnerLink ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setPartner(data);
                      }}
                      className="max-w-xs flex-1 bg-brand-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-brand-500 sm:w-full"
                    >
                      Buy &amp; Train
                    </button>
                  ) : null}
                  {!props.primary.fields.data.active &&
                    props.primary.fields?.link && (
                      <a
                        href={`${props.primary.fields.link.url}?${query}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleClick(
                            `${props.primary.fields.link.url}?${query}`
                          );
                        }}
                        className="max-w-xs flex-1 bg-brand-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-brand-500 sm:w-full"
                      >
                        Pre order
                      </a>
                    )}
                </div>
              </div>

              <section aria-labelledby="details-heading" className="mt-12">
                <h2 id="details-heading" className="sr-only">
                  Additional course details
                </h2>

                <div className="mt-8 border-t border-gray-200 pt-8">
                  <div className="prose prose-sm text-gray-500">
                    {documentToReactComponents(
                      props.fields.description,
                      richContentOptions
                    )}
                  </div>
                </div>

                {/* Policies */}
                <section aria-labelledby="policies-heading" className="mt-10">
                  <h2 id="policies-heading" className="sr-only">
                    Course Features
                  </h2>

                  <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                    {policies(vtct).map((policy, index) => (
                      <div
                        key={policy.name}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"
                      >
                        <dt>
                          <policy.icon
                            className="mx-auto h-6 w-6 flex-shrink-0 text-gray-400"
                            aria-hidden="true"
                          />
                          <span className="mt-4 text-sm font-medium text-gray-900">
                            {policy.name}
                          </span>
                        </dt>
                        <dd className="mt-1 text-sm text-gray-500">
                          {policy.description}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </section>
            </div>
          </div>
        </div>
        <nav
          aria-label="Offers"
          className="order-last lg:order-first border-t border-b mb-16 md:mb-4"
        >
          <div className="max-w-7xl mx-auto lg:px-8">
            <ul
              role="list"
              className="grid grid-cols-1 divide-y divide-gray-200 lg:grid-cols-3 lg:divide-y-0 lg:divide-x"
            >
              {offers(vtct).map((offer) => (
                <li key={offer.name} className="flex flex-col">
                  <a
                    href={offer.href}
                    className="relative flex-1 flex flex-col justify-center bg-white py-6 px-4 text-center focus:z-10"
                  >
                    <p className="text-sm text-gray-500">{offer.name}</p>
                    <p className="font-semibold text-gray-900">
                      {offer.description}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
      {interested && (
        <Modal
          open={interested}
          close={() => setInterested(false)}
          xlarge
          title="Register Interest"
          subTitle={
            interested
              ? `Get notified when ${interested.fields.title} is available`
              : null
          }
        >
          <form
            action="https://manage.kmail-lists.com/subscriptions/subscribe?a=XxnURE&g=WgFGLa"
            method="post"
            target="_blank"
          >
            <div className="grid gap-4 grid-cols-1 my-4">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Name</span>
                  <input
                    type="text"
                    // {...form.register("name", { required: true })}
                    name="FIRST_NAME"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your name"
                  />
                </label>
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Email</span>
                  <input
                    type="email"
                    // {...form.register("email", { required: true })}
                    name="EMAIL"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your email"
                  />
                </label>
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Phone number</span>
                  <input
                    type="tel"
                    // {...form.register("phone", { required: true })}
                    name="PHONE_NUMBER"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your phone number"
                  />
                </label>
              </div>
              <div className="col-span-1 flex justify-end">
                <div className="w-64">
                  <button className="w-full flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-600 hover:bg-brand-900">
                    Register interest
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}
      {partner && (
        <PartnerModal data={partner} onClose={() => setPartner(null)} />
      )}
    </>
  );
}
